import { StandardEvents } from '@shopify/events';
import { getWishlistStore } from '@theme/levayo-wishlist-store-v1';

class LevayoGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.variantGeneration = (this.variantGeneration || 0) + 1;
    const slides = [...this.querySelectorAll('.lv-gallery__slide')];
    const thumbs = [...this.querySelectorAll('[data-index]')];
    let current = 0;
    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== current; });
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === current)));
      this.querySelector('.lv-gallery__count').textContent = `${current + 1} / ${slides.length}`;
    };
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index), { signal }));
    const selectedIndex = slides.findIndex(slide => slide.dataset.imageId === this.dataset.selectedImage && this.dataset.selectedImage);
    if (selectedIndex >= 0) show(selectedIndex);
    const stage = this.querySelector('.lv-gallery__stage');
    let swipe = null;
    const resetSwipe = () => { swipe = null; };
    stage?.addEventListener('pointerdown', event => {
      if (!event.isPrimary) { resetSwipe(); return; }
      if (event.pointerType !== 'touch' || !matchMedia('(max-width: 749px)').matches || slides.length < 2 || event.target.closest('button,a,video,input,select')) return;
      swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
      stage.setPointerCapture(event.pointerId);
    }, { signal: this.controller.signal });
    stage?.addEventListener('pointerup', event => {
      if (!swipe || swipe.id !== event.pointerId) return;
      const dx = event.clientX - swipe.x;
      const dy = event.clientY - swipe.y;
      resetSwipe();
      if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.4) show(current + (dx < 0 ? 1 : -1));
    }, { signal: this.controller.signal });
    stage?.addEventListener('pointercancel', resetSwipe, { signal: this.controller.signal });
    stage?.addEventListener('lostpointercapture', resetSwipe, { signal: this.controller.signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, event => {
      if (!(event.target instanceof Element) || event.target.closest('product-card,quick-add-component,dialog') || !event.promise) return;
      if (event.product?.id && String(event.product.id).replace('gid://shopify/Product/', '') !== this.dataset.product) return;
      const generation = ++this.variantGeneration;
      event.promise.then(({ detail }) => {
        const updated = detail?.html?.querySelector('levayo-gallery');
        if (updated && this.isConnected && generation === this.variantGeneration && updated.dataset.product === this.dataset.product) this.replaceWith(updated);
      }).catch(error => { if (error?.name !== 'AbortError') console.warn('Galerie konnte nicht aktualisiert werden.', error); });
    }, { signal: this.controller.signal });
    this.querySelector('.lv-gallery__prev')?.addEventListener('click', () => show(current - 1), { signal });
    this.querySelector('.lv-gallery__next')?.addEventListener('click', () => show(current + 1), { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); show(current + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, { signal });
    const heart = this.querySelector('.lv-gallery__favorite');
    const store = getWishlistStore();
    const update = (saved) => {
      heart.setAttribute('aria-pressed', String(saved));
      heart.setAttribute('aria-label', saved ? this.dataset.favoriteRemove : this.dataset.favoriteAdd);
    };
    this.unsubscribeWishlist?.();
    this.unsubscribeWishlist = store.subscribe(state => {
      update(state.products.some(product => product.id === this.dataset.product));
      heart.disabled = state.pending || !['guest', 'account'].includes(state.mode);
      heart.setAttribute('aria-busy', String(state.pending || state.mode === 'checking'));
      if (state.mode === 'guest') heart.setAttribute('aria-haspopup', 'dialog');
      else heart.removeAttribute('aria-haspopup');
    });
    void store.ready();
    const requestLogin = (product) => {
      if (!this.isConnected || signal.aborted) return;
      const status = this.querySelector('[data-favorite-status]');
      if (status) status.textContent = '';
      document.dispatchEvent(new CustomEvent('levayo:wishlist-login', { detail: { product, trigger: heart } }));
    };
    heart.addEventListener('click', async () => {
      try {
        const product = {
          id: this.dataset.product, url: this.dataset.productUrl,
          title: this.dataset.productTitle, image: this.dataset.productImage, price: this.dataset.productPrice,
          selectedVariantId: this.dataset.selectedVariantId, variantTitle: this.dataset.variantTitle,
          priceCents: Number(this.dataset.priceCents), currency: this.dataset.currency,
          country: this.dataset.country, marketId: this.dataset.marketId, available: this.dataset.available === 'true'
        };
        let state = store.snapshot();
        if (state.mode === 'checking') {
          await store.ready();
          if (!this.isConnected || signal.aborted) return;
          state = store.snapshot();
        }
        if (state.mode === 'guest') { requestLogin(product); return; }
        if (state.mode !== 'account') {
          this.querySelector('[data-favorite-status]').textContent = this.dataset.favoriteError;
          return;
        }
        const saved = !state.products.some(item => item.id === this.dataset.product);
        const result = await store.setSaved(product, saved);
        if (!this.isConnected || signal.aborted) return;
        if (!result.ok && result.error === 'LOGIN_REQUIRED') { requestLogin(product); return; }
        this.querySelector('[data-favorite-status]').textContent = result.ok
          ? saved ? this.dataset.favoriteSaved : this.dataset.favoriteRemoved
          : this.dataset.favoriteError;
      } catch {
        if (!this.isConnected || signal.aborted) return;
        this.querySelector('[data-favorite-status]').textContent = this.dataset.favoriteError;
      }
    }, { signal });
  }
  disconnectedCallback() {
    this.unsubscribeWishlist?.(); this.unsubscribeWishlist = null;
    this.controller?.abort();
    this.variantGeneration++;
    this.initialized = false;
  }
}
if (!customElements.get('levayo-gallery')) customElements.define('levayo-gallery', LevayoGallery);
